import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { ITrigger } from '../../../../../../../../../../common/common-interfaces/trigger';
import { IPackage } from '../../../../../../../../../../common/common-interfaces/package';
import { DrawerModule } from 'primeng/drawer';
import { translatedOptions } from '../translated-options';

export interface OpenEmailEditorEvent {
    trigger: ITrigger;
    beefreeTemplate: any;
}

type TriggerData = ITrigger['triggerData'];
type ActionData = ITrigger['actionData'];
type BeforeBookingData = NonNullable<TriggerData['beforeBookingData']>;
type SendEmailData = NonNullable<ActionData['sendEmailData']>;
type ScheduleEmailData = NonNullable<ActionData['scheduleEmailData']>;
type SendSmsData = NonNullable<ActionData['sendSmsData']>;
type ScheduleSmsData = NonNullable<ActionData['scheduleSmsData']>;

@Component({
    selector: 'edit-trigger-dialog',
    templateUrl: './edit-trigger-dialog.html',
    imports: [FormsModule, TranslatePipe, ButtonDirective, Dialog, InputNumber, InputText, Select, Textarea, DrawerModule]
})
export class EditTriggerDialogComponent {
    visible = model<boolean>(false);

    trigger = input.required<ITrigger>();
    // Seeded from the incoming trigger, but freely editable locally until saved.
    editedTrigger = linkedSignal(() => this.trigger());

    triggerTypeOptions = input<{ label: string; value: string }[]>([]);
    actionTypeOptions = input<{ label: string; value: string }[]>([]);
    availablePackages = input<IPackage[]>([]);

    triggerChanged = output<ITrigger>();
    openEmailEditor = output<OpenEmailEditorEvent>();
    showPlaceholders = output<void>();
    saved = output<void>();
    cancelled = output<void>();

    readonly timeUnitOptions = translatedOptions([
        { label: 'MINUTES', value: 'minutes' },
        { label: 'HOURS', value: 'hours' },
        { label: 'DAYS', value: 'days' },
        { label: 'WEEKS', value: 'weeks' },
        { label: 'MONTHS', value: 'months' }
    ]);

    readonly receiverOptions = translatedOptions([
        { label: 'CUSTOMER', value: 'CUSTOMER' },
        { label: 'CUSTOM_RECEIVER', value: 'CUSTOM' }
    ]);

    // ── Derived read-only state ──────────────────────────────────────────────

    isBeforeBookingStart = computed(() => this.editedTrigger().triggerData?.name === 'BEFORE_BOOKING_START');
    isSendEmail = computed(() => this.editedTrigger().actionData?.name === 'SEND_EMAIL');
    isScheduleEmail = computed(() => this.editedTrigger().actionData?.name === 'SCHEDULE_EMAIL');
    isSendSms = computed(() => this.editedTrigger().actionData?.name === 'SEND_SMS');
    isScheduleSms = computed(() => this.editedTrigger().actionData?.name === 'SCHEDULE_SMS');

    showPackages = computed(() =>
        ['BEFORE_BOOKING_START', 'BOOKING_CREATED', 'BOOKING_CHANGED', 'BOOKING_CANCELLED', 'BOOKING_CHECKED_IN'].includes(
            this.editedTrigger().triggerData?.name ?? ''
        )
    );

    // Read side of the old packageIds get/set pair: a plain derived signal,
    // no hidden side effects on read.
    packageIds = computed<string[]>(() => {
        const td = this.editedTrigger().triggerData;
        switch (td.name) {
            case 'BEFORE_BOOKING_START':
                return td.beforeBookingData?.specificPackageIds ?? [];
            case 'BOOKING_CREATED':
                return td.bookingCreatedData?.specificPackageIds ?? [];
            case 'BOOKING_CHANGED':
                return td.bookingChangedData?.specificPackageIds ?? [];
            case 'BOOKING_CANCELLED':
                return td.bookingCancelledData?.specificPackageIds ?? [];
            case 'BOOKING_CHECKED_IN':
                return td.bookingCheckedInData?.specificPackageIds ?? [];
            default:
                return [];
        }
    });

    // Write side: an explicit method instead of a setter, so the mutation is
    // visible at the call site (updatePackageIds(...)) rather than hidden
    // behind an innocent-looking assignment.
    updatePackageIds(ids: string[]): void {
        const td = this.editedTrigger().triggerData;
        switch (td.name) {
            case 'BEFORE_BOOKING_START':
                this.updateTriggerData({ beforeBookingData: { ...td.beforeBookingData, specificPackageIds: ids } } as Partial<TriggerData>);
                break;
            case 'BOOKING_CREATED':
                this.updateTriggerData({
                    bookingCreatedData: { ...td.bookingCreatedData, specificPackageIds: ids }
                } as Partial<TriggerData>);
                break;
            case 'BOOKING_CHANGED':
                this.updateTriggerData({
                    bookingChangedData: { ...td.bookingChangedData, specificPackageIds: ids }
                } as Partial<TriggerData>);
                break;
            case 'BOOKING_CANCELLED':
                this.updateTriggerData({
                    bookingCancelledData: { ...td.bookingCancelledData, specificPackageIds: ids }
                } as Partial<TriggerData>);
                break;
            case 'BOOKING_CHECKED_IN':
                this.updateTriggerData({
                    bookingCheckedInData: { ...td.bookingCheckedInData, specificPackageIds: ids }
                } as Partial<TriggerData>);
                break;
        }
    }

    // ── Immutable update helpers ─────────────────────────────────────────────
    // Every field edit in the template goes through one of these instead of
    // mutating editedTrigger()'s nested objects directly (which wouldn't
    // notify the signal, since the object reference would stay the same).

    updateTrigger(patch: Partial<ITrigger>): void {
        this.editedTrigger.update((t) => ({ ...t, ...patch }));
        this.onFieldChange();
    }

    updateTriggerData(patch: Partial<TriggerData>): void {
        this.editedTrigger.update((t) => ({ ...t, triggerData: { ...t.triggerData, ...patch } }));
        this.onFieldChange();
    }

    updateActionData(patch: Partial<ActionData>): void {
        this.editedTrigger.update((t) => ({ ...t, actionData: { ...t.actionData, ...patch } }));
        this.onFieldChange();
    }

    updateTriggerEventType(name: TriggerData['name']): void {
        this.updateTriggerData({ name } as Partial<TriggerData>);
    }

    updateActionType(name: ActionData['name']): void {
        this.updateActionData({ name } as Partial<ActionData>);
    }

    updateBeforeBookingData(patch: Partial<BeforeBookingData>): void {
        const td = this.editedTrigger().triggerData;
        this.updateTriggerData({
            beforeBookingData: { ...td.beforeBookingData, ...patch } as BeforeBookingData
        } as Partial<TriggerData>);
    }

    updateSendEmailData(patch: Partial<SendEmailData>): void {
        const ad = this.editedTrigger().actionData;
        this.updateActionData({
            sendEmailData: { ...ad.sendEmailData, ...patch } as SendEmailData
        } as Partial<ActionData>);
    }

    updateScheduleEmailData(patch: Partial<ScheduleEmailData>): void {
        const ad = this.editedTrigger().actionData;
        this.updateActionData({
            scheduleEmailData: { ...ad.scheduleEmailData, ...patch } as ScheduleEmailData
        } as Partial<ActionData>);
    }

    updateSendSmsData(patch: Partial<SendSmsData>): void {
        const ad = this.editedTrigger().actionData;
        this.updateActionData({
            sendSmsData: { ...ad.sendSmsData, ...patch } as SendSmsData
        } as Partial<ActionData>);
    }

    updateScheduleSmsData(patch: Partial<ScheduleSmsData>): void {
        const ad = this.editedTrigger().actionData;
        this.updateActionData({
            scheduleSmsData: { ...ad.scheduleSmsData, ...patch } as ScheduleSmsData
        } as Partial<ActionData>);
    }

    // ── Events ───────────────────────────────────────────────────────────────

    onFieldChange(): void {
        this.triggerChanged.emit(this.editedTrigger());
    }

    onClose(): void {
        this.cancelled.emit();
        this.visible.set(false);
    }

    onSave(): void {
        this.saved.emit();
        this.visible.set(false);
    }

    onOpenEmailEditor(): void {
        const trigger = this.editedTrigger();
        const actionData = trigger.actionData;
        const beefreeTemplate =
            actionData.name === 'SEND_EMAIL'
                ? actionData.sendEmailData?.thirdParty?.beefree
                : actionData.scheduleEmailData?.thirdParty?.beefree;

        this.openEmailEditor.emit({ trigger, beefreeTemplate });
    }

    getNumberOfSmsSegments(message: string): number {
        if (!message) {
            return 0;
        }
        return Math.ceil(message.length / 160);
    }
}
